import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

export function useProfileOverlayPreferences(brandId?: string | null) {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['profile-overlay-preferences', user?.id, brandId],
    queryFn: async () => {
      if (!user?.id) return null;
      const [profileRes, personalControlsRes, personalWidgetsRes] = await Promise.all([
        supabase
          .from('profiles')
          .select('controls_dashboard_id, widgets_dashboard_id')
          .eq('id', user.id)
          .single(),
        brandId
          ? supabase
              .from('os_experiments_dashboards')
              .select('id')
              .eq('brand_id', brandId)
              .eq('type', 'controls')
              .eq('user_id', user.id)
              .order('updated_at', { ascending: false })
              .limit(1)
              .maybeSingle()
          : Promise.resolve({ data: null }),
        brandId
          ? supabase
              .from('os_experiments_dashboards')
              .select('id')
              .eq('brand_id', brandId)
              .eq('type', 'widgets')
              .eq('user_id', user.id)
              .order('updated_at', { ascending: false })
              .limit(1)
              .maybeSingle()
          : Promise.resolve({ data: null }),
      ]);

      const profile = profileRes.data;
      if (profileRes.error) throw profileRes.error;

      const personalControlsId = personalControlsRes.data?.id ?? null;
      const personalWidgetsId = personalWidgetsRes.data?.id ?? null;

      return {
        ...profile,
        effectiveControlsDashboardId: personalControlsId ?? profile?.controls_dashboard_id ?? null,
        effectiveWidgetsDashboardId: personalWidgetsId ?? profile?.widgets_dashboard_id ?? null,
      };
    },
    enabled: !!user?.id,
  });

  const updateControlsMutation = useMutation({
    mutationFn: async (controlsDashboardId: string | null) => {
      if (!user?.id) throw new Error('User not authenticated');
      const { error } = await supabase
        .from('profiles')
        .update({ controls_dashboard_id: controlsDashboardId })
        .eq('id', user.id);
      if (error) throw error;
      return controlsDashboardId;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['profile-overlay-preferences', user?.id, brandId] });
      toast.success('Controls dashboard updated');
    },
    onError: (error) => {
      toast.error('Failed to update: ' + (error as Error).message);
    },
  });

  const updateWidgetsMutation = useMutation({
    mutationFn: async (widgetsDashboardId: string | null) => {
      if (!user?.id) throw new Error('User not authenticated');
      const { error } = await supabase
        .from('profiles')
        .update({ widgets_dashboard_id: widgetsDashboardId })
        .eq('id', user.id);
      if (error) throw error;
      return widgetsDashboardId;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['profile-overlay-preferences', user?.id, brandId] });
      toast.success('Widgets dashboard updated');
    },
    onError: (error) => {
      toast.error('Failed to update: ' + (error as Error).message);
    },
  });

  return {
    controlsDashboardId: data?.effectiveControlsDashboardId ?? data?.controls_dashboard_id ?? null,
    widgetsDashboardId: data?.effectiveWidgetsDashboardId ?? data?.widgets_dashboard_id ?? null,
    setControlsDashboard: updateControlsMutation.mutate,
    setWidgetsDashboard: updateWidgetsMutation.mutate,
    isLoading,
  };
}
